import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import clsx from 'clsx';
import AnthraciteIconButton from '@components/Shared/Blocks/IconButton';
import { SwiperInjectStyles } from '@themes/common/lazy-swiper';
function ImageItem({ id, image, style, activeTitle, comparedImage }) {
    const [sliderValue, setSliderValue] = useState(50);
    const handleSliderValue = useCallback((e) => {
        const value = parseFloat(e.target.value);
        setSliderValue(value);
    }, []);
    const compareImageComponent = (_jsx("div", Object.assign({ className: 'sb-ic--container', style: {
            '--position': `${sliderValue}%`,
        } }, { children: _jsxs("div", Object.assign({ className: 'sb-ic--image-container' }, { children: [_jsx("div", Object.assign({ className: 'sb-ic--image sb-ic--image-left' }, { children: _jsx("img", { src: image, alt: style, loading: 'lazy' }) })), _jsx("div", Object.assign({ className: 'sb-ic--image sb-ic--image-right' }, { children: comparedImage && _jsx("img", { src: comparedImage, alt: style, loading: 'lazy' }) })), _jsx("input", { type: 'range', min: 0, max: 100, step: '0.2', value: sliderValue, "aria-label": 'Percentage of before photo shown', className: 'sb-ic--input-slider', onChange: handleSliderValue }), _jsx("div", { className: 'sb-ic--vertical-line' }), _jsx("div", { className: 'sb-ic--vertical-line sb-ic--vertical-line-bottom' }), _jsx("div", Object.assign({ className: 'sb-ic--button material-icons', "aria-hidden": 'true' }, { children: "switch_camera" }))] })) })));
    const imageCompare = comparedImage ? (_jsxs(_Fragment, { children: [_jsx("img", { src: image, alt: style, loading: 'lazy' }), compareImageComponent] })) : (_jsx("div", Object.assign({ className: 'swiper-zoom-container' }, { children: _jsx("img", { src: image, alt: style, loading: 'lazy' }) })));
    return (_jsx("swiper-slide", Object.assign({ id: id }, { children: _jsxs("div", Object.assign({ className: 'image-container' }, { children: [activeTitle && _jsx("div", Object.assign({ className: 'overlay-title' }, { children: style })), imageCompare] })) })));
}
const SlidesContainer = forwardRef(function SlidesContainer({ firstRoom, secondRoom, galleryWidth, slideIndex, setSlideIndex }, ref) {
    const swiperInitialized = useRef(false);
    const swiperElement = useRef(null);
    useImperativeHandle(ref, () => swiperElement.current, []);
    useEffect(() => {
        var _a;
        if (swiperElement.current && !swiperInitialized.current) {
            Object.assign(swiperElement.current, {
                injectStyles: [
                    ...SwiperInjectStyles,
                    `.swiper-button-prev,.swiper-button-next {
            color: #000;
            text-shadow: #fff 1px 0 10px;
          }`,
                ],
                initialSlide: slideIndex,
                observer: true,
                observeParents: true,
                observeSlideChildren: true,
                loop: true,
                navigation: true,
                slidesPerView: 1,
                effect: 'fade',
                fadeEffect: {
                    crossFade: true,
                },
                zoom: true,
                pagination: true,
                autoHieght: true,
                allowTouchMove: secondRoom && firstRoom ? false : true,
                on: {
                    slideChange: (swiper) => setSlideIndex(swiper.realIndex),
                },
            });
            swiperElement.current.initialize();
            swiperInitialized.current = true;
        }
        else if ((_a = swiperElement.current) === null || _a === void 0 ? void 0 : _a.swiper) {
            if (swiperElement.current.swiper.realIndex !== slideIndex) {
                swiperElement.current.swiper.slideTo(slideIndex);
            }
        }
    }, [firstRoom, secondRoom, setSlideIndex, slideIndex, swiperElement]);
    return firstRoom || secondRoom ? (_jsx("swiper-container", Object.assign({ ref: swiperElement, init: 'false' }, { children: (firstRoom || secondRoom).images.map((image, imageIndex) => (_jsx(ImageItem, { id: image, style: (firstRoom || secondRoom).room.style, image: image, room: firstRoom || secondRoom, comparedImage: firstRoom && secondRoom ? secondRoom.images[imageIndex] : null, galleryWidth: galleryWidth, active: imageIndex === slideIndex }, image))) }))) : (_jsx(_Fragment, { children: " " }));
});
const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1),
    justifyContent: 'flex-end',
    position: 'absolute',
    right: 10,
    top: 10,
    zIndex: 10,
}));
function Thumbnails({ breakpoint, slideIndex, secondRoom, setSlideIndex, firstRoom }) {
    const swiperInitialized = useRef(false);
    const swiperElement = useRef(null);
    useEffect(() => {
        var _a;
        if (swiperElement.current && !swiperInitialized.current) {
            swiperInitialized.current = true;
            Object.assign(swiperElement.current, {
                injectStyles: [
                    ...SwiperInjectStyles,
                    `.swiper-button-prev,.swiper-button-next {
            color: #000;
            text-shadow: #fff 1px 0 10px;
            width: 25px;
            --swiper-navigation-size: 1rem;
          }`,
                ],
                initialSlide: slideIndex,
                slidesPerView: 'auto',
                navigation: true,
                allowTouchMove: firstRoom && secondRoom ? false : true,
                direction: breakpoint === 'mobile' ? 'horizontal' : 'vertical',
                on: {
                    slideChange: (swiper) => setSlideIndex(swiper.realIndex),
                },
            });
            swiperElement.current.initialize();
        }
        else if ((_a = swiperElement.current) === null || _a === void 0 ? void 0 : _a.swiper) {
            if (swiperElement.current.swiper.realIndex !== slideIndex) {
                swiperElement.current.swiper.slideTo(slideIndex);
            }
        }
    }, [breakpoint, firstRoom, secondRoom, setSlideIndex, slideIndex]);
    return firstRoom || secondRoom ? (_jsx("swiper-container", Object.assign({ ref: swiperElement, init: 'false' }, { children: (firstRoom || secondRoom).images.map((image, imageIndex) => (_jsx("swiper-slide", { children: _jsx("div", Object.assign({ className: clsx('gallery-images--item', {
                    active: imageIndex === slideIndex,
                }), onClick: (e) => {
                    e.preventDefault();
                    setSlideIndex(imageIndex);
                }, style: {
                    backgroundImage: breakpoint === 'mobile' ? `url("${image}")` : undefined,
                } }, { children: breakpoint !== 'mobile' && _jsx("img", { src: image, alt: (firstRoom || secondRoom).room.style, loading: 'lazy' }) }), image) }, image))) }))) : (_jsx(_Fragment, {}));
}
const ZoomProductGalleryGallerySection = forwardRef(function ZoomProductGalleryGallerySection({ firstRoom, secondRoom, galleryWidth, setSlideIndex, slideIndex, fullscreen, toggleFullscreen, breakpoint }, ref) {
    const [dimension, setDimension] = useState({
        height: jQuery(globalThis).height(),
        width: jQuery(globalThis).width(),
    });
    useEffect(() => {
        const handleResize = () => {
            setDimension({
                height: jQuery(globalThis).height(),
                width: jQuery(globalThis).width(),
            });
        };
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);
    return (_jsxs(Dialog, Object.assign({ fullScreen: true, open: fullscreen, onClose: toggleFullscreen, disableScrollLock: false }, { children: [_jsx(DrawerHeader, { children: _jsx(AnthraciteIconButton, Object.assign({ onClick: toggleFullscreen, className: 'button-icon' }, { children: _jsx(CloseIcon, {}) })) }), _jsxs("div", Object.assign({ className: clsx('sb-product-gallery zoom', breakpoint), style: {
                    '--full-height': `${dimension.height}px`,
                    '--full-width': `${dimension.width}px`,
                } }, { children: [_jsx("div", Object.assign({ className: 'gallery-images' }, { children: _jsx(Thumbnails, { breakpoint: breakpoint, firstRoom: firstRoom, secondRoom: secondRoom, slideIndex: slideIndex, setSlideIndex: setSlideIndex }) })), _jsx("div", Object.assign({ className: 'gallery-section' }, { children: _jsx("div", Object.assign({ className: 'image-wrapper' }, { children: _jsx(SlidesContainer, { ref: ref, firstRoom: firstRoom, secondRoom: secondRoom, galleryWidth: galleryWidth, slideIndex: slideIndex, setSlideIndex: setSlideIndex }) })) }))] }))] })));
});
export default ZoomProductGalleryGallerySection;
